import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import useSeo from "@/lib/useSeo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Sparkles, ArrowRight, ArrowLeft, Loader2, Lightbulb, Users, AlertCircle, Target, Zap, Rocket } from "lucide-react";

const QUESTIONS = [
  { key: "topic", q: "What's something you've learned, experienced, or enjoy that you could turn into something useful for someone else?", ph: "e.g. Growing herbs on a small balcony", type: "textarea" },
  { key: "audience", q: "Who do you most want to help?", ph: "e.g. Total beginners with no garden and little space", type: "textarea" },
  { key: "problem", q: "What's the biggest problem they're struggling with?", ph: "e.g. They kill every plant and don't know where to start", type: "textarea" },
  { key: "result", q: "What result would they love to achieve?", ph: "e.g. Fresh herbs they actually grow and use every week", type: "textarea" },
  { key: "tried", q: "Have you ever tried to teach or share this knowledge before?", type: "yesno" },
];

export default function IdeaGenerator() {
  const [step, setStep] = useState("intro"); // intro | questions | capture | result
  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState({ topic: "", audience: "", problem: "", result: "", tried: "" });
  const [lead, setLead] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useSeo({
    title: "Free Course Idea Generator — Find Your Mini-Course Idea in 10 Minutes | Hook2Income AI",
    description:
      "Answer 5 simple questions and our free AI Course Idea Generator finds your strongest mini-course idea, your ideal audience and a ready-to-use hook — no account needed to start.",
    path: "/idea-generator",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "How does the free Course Idea Generator work?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Answer 5 quick questions about what you know and who you want to help. Hook2Income AI then finds your strongest course idea, your audience, the core problem to solve and a ready-to-use hook.",
          },
        },
        {
          "@type": "Question",
          name: "Is the Course Idea Generator free?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. The Course Idea Generator is completely free and you don't need an account to start. You can then turn your idea into a full mini-course with Hook2Income AI.",
          },
        },
        {
          "@type": "Question",
          name: "Do I need a camera or video editing skills?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "No. Hook2Income AI is built for beginners — no camera, voiceover or editing required. You bring the topic and the AI guides you to ready-to-publish content.",
          },
        },
      ],
    },
  });

  const set = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));
  const cur = QUESTIONS[qIdx];
  const curFilled = cur.type === "yesno" ? !!answers[cur.key] : answers[cur.key].trim().length > 0;

  const nextQ = () => {
    if (qIdx < QUESTIONS.length - 1) setQIdx(qIdx + 1);
    else setStep("capture");
  };
  const prevQ = () => {
    if (qIdx > 0) setQIdx(qIdx - 1);
    else setStep("intro");
  };

  const generate = async () => {
    if (!lead.name.trim() || !lead.email.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/idea-generator/generate", { ...answers, ...lead });
      setResult(data.result);
      setStep("result");
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(formatApiErrorDetail(e) || "Something went wrong — please try again.");
    } finally {
      setLoading(false);
    }
  };

  const signupUrl = () => {
    try {
      localStorage.setItem("h2i_idea_prefill", JSON.stringify({ answers, result }));
    } catch (e) { console.warn("Could not save Idea Generator prefill to localStorage:", e); }
    return "/register?from=idea-generator";
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] text-[#1C1917]" data-testid="idea-generator-page"
      style={{ fontFamily: "'Poppins', ui-sans-serif, system-ui, sans-serif" }}>
      {/* Minimal brand mark only — no nav/footer */}
      <div className="mx-auto flex max-w-2xl items-center gap-2 px-5 pt-8">
        <span className="text-lg font-extrabold tracking-tight">Hook2Income<span className="text-primary"> AI</span></span>
      </div>

      <div className="mx-auto max-w-2xl px-5 py-8 sm:py-12">
        {/* STEP 1 — Intro */}
        {step === "intro" && (
          <div className="fade-up text-center" data-testid="ig-intro">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <Sparkles size={26} />
            </div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">The Free Course Idea Generator</p>
            <h1 className="mt-3 font-head text-4xl font-bold leading-tight sm:text-5xl">Discover Your Course Idea in 10 Minutes</h1>
            <p className="mx-auto mt-4 max-w-lg text-base text-muted-foreground sm:text-lg">
              Answer 5 simple questions and Hook2Income AI will find your strongest course idea, your audience, and your Hook — completely free.
            </p>
            <Button onClick={() => { setStep("questions"); setQIdx(0); }} data-testid="ig-start-btn"
              className="mt-8 rounded-full bg-primary px-8 py-6 text-base font-semibold hover:bg-primary/90">
              Get my course idea <ArrowRight size={18} className="ml-1.5" />
            </Button>
            <p className="mt-3 text-xs text-muted-foreground">Free · no account needed to start</p>
          </div>
        )}

        {/* STEP 2 — Questions */}
        {step === "questions" && (
          <div className="fade-up" data-testid="ig-questions">
            <div className="mb-6 flex items-center gap-2">
              {QUESTIONS.map((_, i) => (
                <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= qIdx ? "bg-primary" : "bg-border"}`} />
              ))}
            </div>
            <p className="text-sm font-semibold text-muted-foreground">Question {qIdx + 1} of {QUESTIONS.length}</p>
            <h2 className="mt-2 font-head text-2xl font-bold sm:text-3xl">{cur.q}</h2>

            <div className="mt-6">
              {cur.type === "yesno" ? (
                <div className="flex gap-3">
                  {["Yes", "No"].map((opt) => (
                    <button key={opt} onClick={() => set("tried", opt)} data-testid={`ig-tried-${opt.toLowerCase()}`}
                      className={`flex-1 rounded-2xl border-2 px-6 py-4 text-base font-semibold transition-all ${answers.tried === opt ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-primary/40"}`}>
                      {opt}
                    </button>
                  ))}
                </div>
              ) : (
                <Textarea data-testid={`ig-q-${cur.key}`} value={answers[cur.key]}
                  onChange={(e) => set(cur.key, e.target.value)} placeholder={cur.ph} rows={4}
                  className="text-base" autoFocus />
              )}
            </div>

            <div className="mt-8 flex items-center justify-between">
              <Button variant="ghost" onClick={prevQ} data-testid="ig-back-btn" className="gap-1.5">
                <ArrowLeft size={16} /> Back
              </Button>
              <Button onClick={nextQ} disabled={!curFilled} data-testid="ig-next-btn"
                className="gap-1.5 rounded-full bg-primary px-7 py-5 font-semibold hover:bg-primary/90">
                {qIdx === QUESTIONS.length - 1 ? "Almost there" : "Next"} <ArrowRight size={16} />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3 — Email capture */}
        {step === "capture" && (
          <div className="fade-up" data-testid="ig-capture">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <Lightbulb size={26} />
            </div>
            <h2 className="font-head text-3xl font-bold">Where should we send your course idea?</h2>
            <p className="mt-2 text-muted-foreground">We're crafting it now — pop your details in and we'll show it on the next screen.</p>
            <div className="mt-6 space-y-4">
              <div className="space-y-2">
                <Label>First name</Label>
                <Input data-testid="ig-name" value={lead.name} onChange={(e) => setLead((l) => ({ ...l, name: e.target.value }))}
                  placeholder="e.g. Sam" className="text-base" />
              </div>
              <div className="space-y-2">
                <Label>Email address</Label>
                <Input data-testid="ig-email" type="email" value={lead.email} onChange={(e) => setLead((l) => ({ ...l, email: e.target.value }))}
                  placeholder="you@example.com" className="text-base" />
              </div>
            </div>
            <div className="mt-8 flex items-center justify-between">
              <Button variant="ghost" onClick={() => setStep("questions")} data-testid="ig-capture-back" className="gap-1.5">
                <ArrowLeft size={16} /> Back
              </Button>
              <Button onClick={generate} disabled={loading || !lead.name.trim() || !lead.email.trim()} data-testid="ig-generate-btn"
                className="gap-1.5 rounded-full bg-primary px-7 py-5 font-semibold hover:bg-primary/90">
                {loading ? <><Loader2 size={18} className="animate-spin" /> Finding your idea…</> : <>Show my course idea <ArrowRight size={16} /></>}
              </Button>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">We'll email you a copy. No spam — unsubscribe anytime.</p>
          </div>
        )}

        {/* STEP 4 + 5 — Result + CTA */}
        {step === "result" && result && (
          <div className="fade-up" data-testid="ig-result">
            <div className="text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Your course idea is ready 🎉</p>
              <h2 className="mt-2 font-head text-3xl font-bold sm:text-4xl">Here's your strongest idea, {lead.name || "friend"}</h2>
            </div>

            <div className="mt-8 overflow-hidden rounded-3xl border border-border bg-white shadow-sm">
              <div className="bg-[#1C1917] px-6 py-4">
                <span className="text-sm font-extrabold text-white">Hook2Income<span className="text-primary"> AI</span></span>
              </div>
              <div className="space-y-6 p-6 sm:p-8">
                <ResultRow icon={Lightbulb} label="Your course idea" value={result.course_idea} big />
                <ResultRow icon={Users} label="Your audience" value={result.audience} />
                <ResultRow icon={AlertCircle} label="Core problem" value={result.core_problem} />
                <ResultRow icon={Target} label="Transformation promise" value={result.transformation} />
                <ResultRow icon={Zap} label="Your hook" value={result.hook} highlight />
              </div>
            </div>

            <div className="mt-8 text-center">
              <a href={signupUrl()} data-testid="ig-cta-signup">
                <Button className="rounded-full bg-primary px-8 py-6 text-base font-semibold hover:bg-primary/90">
                  <Rocket size={18} className="mr-1.5" /> Turn This Into a Full Course With Hook2Income →
                </Button>
              </a>
              <p className="mt-3 text-sm text-muted-foreground">Free to start · your first Guided journey is on us.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ResultRow({ icon: Icon, label, value, big, highlight }) {
  return (
    <div data-testid={`ig-result-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="mb-1.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-primary">
        <Icon size={15} /> {label}
      </div>
      <p className={`leading-relaxed text-[#1C1917] ${big ? "text-xl font-bold" : "text-base"} ${highlight ? "rounded-xl bg-primary/5 p-4 font-medium" : ""}`}>
        {value}
      </p>
    </div>
  );
}
